import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { environmentContextState, shopifyState, toViewPriceFnState } from '../../recoil/atoms';
import { sizeImage, SIZE_TO_FILTER_BY } from '../../util';
import { Direction, useProductAssets } from '../../hooks/useProductAssets';
import { blankImage } from '../../product_detail/components/ImageLoader';
import styles from './CartPromoItem.module.scss';
import { ViewProduct } from '../../models/ViewProduct.model';
import { Fit } from '../../enums/Fit.enum';
import ColorSwatch from '../../product_detail/components/ColorSwatch';
import { DiscountAmount, DiscountTypes } from '../../discounts';
import { Shopify } from '../../models/Shopify.model';
import { SubContext } from '../../enums/SubContext.enum';
import { useProductSelection } from '../../hooks/useProductSelection';
import { SelectionType } from '../../enums/SelectionType.enum';
import { QuickViewLink } from '../../product_detail/components/QuickViewLink';
import { DivButton } from '../../root/components/DivButton';
import { Icon } from '../../icons/Icon';
import classnames from 'classnames';
import ProductFormButton from '../../product_detail/components/ProductFormButton';
import { cartState } from '../../recoil/selectors';

interface Props {
  product: ViewProduct;
  discount: DiscountAmount;
  selectionKey: string;
}

export const CartPromoItem: React.FC<Props> = ({ product, discount, selectionKey }) => {
  const selectionType = SelectionType.CartPromo;
  const shopify = useRecoilValue(shopifyState);
  const toViewPriceFn = useRecoilValue(toViewPriceFnState);
  const { color: selectedColor, setColor } = useProductSelection(selectionType, selectionKey, product.handle);
  const [colorsExpanded, setColorsExpanded] = useState<boolean>(false);
  const { timedSaleActive } = useRecoilValue(environmentContextState);

  const photos = useProductAssets(product);
  const displayVariants = product.variants.filter((v) => v.size === SIZE_TO_FILTER_BY && (!v.fit || v.fit === Fit.Regular));

  const filteredMannequinPhotos = photos.mannequinPhotos.filter(({ color, direction }) => color === selectedColor && direction === Direction.Front);
  const filteredModelPhotos = photos.modelPhotos.filter(({ color }) => color === selectedColor);
  const mainPhoto = filteredMannequinPhotos[0] ?? filteredModelPhotos[0] ?? { src: blankImage };
  const mainPhotoSrc = sizeImage(mainPhoto.src, '140x160');

  const price = timedSaleActive ? product.timedSalePrice : product.price;

  const cart = useRecoilValue(cartState);
  const maxCartPrice =
    cart.lineItems
      .filter((li) => !li.properties.Bundle)
      .reduce((acc, { final_price, quantity }) => {
        const ppi = final_price / quantity;
        return ppi > acc ? ppi : acc;
      }, 0) / 100;

  const crossoutPrice = product.displayPrice;
  const purchasePrice = toViewPriceFn(
    maxCartPrice === 0 || price <= maxCartPrice
      ? discountedPrice(price, discount, shopify)
      : price - (maxCartPrice - discountedPrice(maxCartPrice, discount, shopify)),
  );

  return (
    <div className={styles.hlCartPromoItem}>
      <div className={styles.hlCartProduct}>
        <div className={styles.hlCartThumbnail}>
          <QuickViewLink
            product={product}
            selectionKey={selectionKey}
            initialColor={selectedColor}
            subContext={SubContext.PromoCartUpsell}
            dataTrackLabel="Quick view"
            dataTrackLocation="Cart promo"
            text=""
            image={<img alt="product" className={styles.hlCartItemImage} src={mainPhotoSrc} />}
          />
        </div>
        <div className={styles.hlCartPromoItemDetails}>
          <div className={styles.hlPromoItemType}>
            <QuickViewLink
              product={product}
              selectionKey={selectionKey}
              initialColor={selectedColor}
              subContext={SubContext.PromoCartUpsell}
              dataTrackLabel="Quick view"
              dataTrackLocation="Cart promo"
              text={product.title}
              buttonType="link"
            />
          </div>
          <div className={styles.hlItemSubtype}>{selectedColor}</div>
          <div className={styles.colorContainer}>
            {displayVariants.slice(0, 4).map((variant) => (
              <ColorSwatch
                key={variant.id}
                color={variant.color}
                active={selectedColor === variant.color}
                activate={() => setColor(variant.color)}
                showSmall={true}
              />
            ))}
            {displayVariants.length > 4 && (
              <>
                <DivButton
                  onClick={() => setColorsExpanded(!colorsExpanded)}
                  style={{ width: '20px', height: '20px', margin: 'auto 0 auto 4px' }}
                  label="View more colors"
                >
                  <Icon name={'chevron-down'} style={{ width: '9px', paddingTop: '1px', paddingLeft: '5px' }} />
                </DivButton>
                <div className={classnames(styles.expandable, { [styles.expanded]: colorsExpanded })}>
                  <DivButton
                    onClick={() => setColorsExpanded(!colorsExpanded)}
                    style={{
                      width: '20px',
                      height: '20px',
                      margin: 'auto 0 auto 4px',
                      position: 'absolute',
                      right: '3px',
                      top: '13px',
                    }}
                    label="View fewer colors"
                  >
                    <Icon name={'chevron-up'} style={{ width: '9px', paddingTop: '1px', paddingLeft: '5px' }} />
                  </DivButton>
                  {displayVariants.map((variant) => (
                    <ColorSwatch
                      key={variant.id}
                      color={variant.color}
                      active={selectedColor === variant.color}
                      activate={() => setColor(variant.color)}
                      showSmall={true}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.hlPromoItemPrice}>
        {crossoutPrice !== purchasePrice && <span className={styles.crossout}>{crossoutPrice}</span>}
        <span>{purchasePrice}</span>
        <ProductFormButton
          forceStepper={true}
          selectionType={selectionType}
          selectionKey={selectionKey}
          subContextOverride={SubContext.CompleteTheLook}
          text={'Pick size'}
          fullWidth={false}
          disablePreCartModal={true}
          size="tiny"
          buttonType="secondary"
        />
      </div>
    </div>
  );
};

const discountedPrice = (priceInCents: number, discount: DiscountAmount, shopify: Shopify | undefined) => {
  switch (discount.type) {
    case DiscountTypes.Cents:
      return priceInCents - centsToCurrency(discount.value, shopify);
    case DiscountTypes.Percentage:
      return priceInCents * (1 - discount.value / 100);
    default:
      return priceInCents;
  }
};
const centsToCurrency = (amountInCents: number, shopify?: Shopify) => Math.floor(amountInCents * (shopify?.currency.rate ?? 1));

export default CartPromoItem;
